import { Component, useEffect, type ErrorInfo, type ReactNode } from 'react'
import { AlertTriangle } from 'lucide-react'
import { Link } from 'react-router-dom'

const reloadKey = (moduleId: string) => `retink:module-reload:${moduleId}`

export function isDynamicImportFailure(error: unknown): boolean {
  const message = error instanceof Error ? `${error.name}: ${error.message}` : String(error)
  return /chunkloaderror|loading chunk|dynamically imported module|importing a module script failed|failed to fetch module script/i.test(message)
}

export function claimModuleReload(moduleId: string, storage: Pick<Storage, 'getItem' | 'setItem'>): boolean {
  try {
    const key = reloadKey(moduleId)
    if (storage.getItem(key)) return false
    storage.setItem(key, '1')
    return true
  } catch {
    return false
  }
}

export function clearModuleReload(moduleId: string, storage: Pick<Storage, 'removeItem'>): void {
  try { storage.removeItem(reloadKey(moduleId)) } catch { /* storage unavailable */ }
}

export function ModuleReady({ moduleId, children }: { moduleId: string; children: ReactNode }) {
  useEffect(() => clearModuleReload(moduleId, window.sessionStorage), [moduleId])
  return children
}

export function ModuleLoadError({ moduleName, onReload }: { moduleName: string; onReload: () => void }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 px-6 py-14 text-center" role="alert">
      <AlertTriangle className="size-6 text-destructive" aria-hidden />
      <h1 className="font-display text-xl font-semibold">{moduleName} couldn’t open</h1>
      <p className="text-sm text-muted-foreground">The app may have updated or your connection may have dropped.</p>
      <div className="flex gap-3">
        <button type="button" onClick={onReload} className="rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground">Reload page</button>
        <Link to="/" className="rounded-md border border-border px-3 py-2 text-sm font-medium">Back to overview</Link>
      </div>
    </div>
  )
}

export class ModuleBoundary extends Component<
  { moduleId: string; moduleName: string; children: ReactNode },
  { failed: boolean }
> {
  state = { failed: false }

  static getDerivedStateFromError() {
    return { failed: true }
  }

  componentDidCatch(error: unknown, _info: ErrorInfo) {
    if (isDynamicImportFailure(error) && claimModuleReload(this.props.moduleId, window.sessionStorage)) {
      window.location.reload()
    }
  }

  render() {
    if (!this.state.failed) return this.props.children
    return <ModuleLoadError moduleName={this.props.moduleName} onReload={() => window.location.reload()} />
  }
}
